export type CompanyStatus = 'draft' | 'onboarding' | 'active' | 'archived';

export type CompanyRecord = {
  id: number;
  name: string;
  slug: string;
  status: CompanyStatus;
  archived_from_status: Exclude<CompanyStatus, 'archived'> | null;
  archived_at: string | null;
  counts: {
    memberships_total: number;
    memberships_active: number;
    groups_total: number;
    groups_active: number;
    invitations_total: number;
    invitations_pending: number;
    imports_total: number;
    locations_total: number;
    locations_active: number;
    services_total: number;
    providers_total: number;
    providers_active: number;
  };
  readiness: {
    label: string;
    score: number;
    blockers: string[];
  };
  can_delete: boolean;
  delete_blockers: string[];
  updated_at?: string | null;
  created_at?: string | null;
};

export type CompanyProfile = {
  id: number;
  name: string;
  slug: string;
  status: CompanyStatus;
  primary_domain: string | null;
  website_url: string | null;
  support_email: string | null;
  support_phone: string | null;
  notes: string | null;
  branding: {
    logo_url: string | null;
    logo_mark_url: string | null;
    has_logo: boolean;
    has_logo_mark: boolean;
  };
  addresses: {
    headquarters: AddressFields;
    operations: AddressFields;
    billing: BillingAddressFields;
  };
  updated_at?: string | null;
  created_at?: string | null;
};

export type CompanyLocationRecord = {
  id: number;
  code: string;
  display_name: string;
  public_label: string | null;
  location_group: string | null;
  timezone: string | null;
  is_active: boolean;
  notes: string | null;
  services_count: number;
  updated_at?: string | null;
  created_at?: string | null;
};

export type CompanyGroupRecord = {
  id: number;
  parent_group_id: number | null;
  parent_name: string | null;
  name: string;
  slug: string;
  kind: string;
  display_kind: string;
  is_department: boolean;
  is_schedule_group: boolean;
  status: 'active' | 'inactive' | 'archived';
  memberships_count: number;
  children_count: number;
  direct_scheduler_admins: Array<{
    id: number;
    name: string;
    email: string | null;
    status: string;
  }>;
  effective_scheduler_admins: Array<{
    id: number;
    name: string;
    email: string | null;
    status: string;
  }>;
  scheduler_admins_count: number;
  updated_at?: string | null;
  created_at?: string | null;
};

export type CompanyRoleAssignmentRecord = {
  id: number;
  role_id: number | null;
  role_key: string | null;
  role_name: string | null;
  scope: string | null;
  permissions: string[];
};

export type CompanyUserGroupMembershipRecord = {
  id: number;
  group_id: number;
  group_name: string | null;
  group_slug: string | null;
  group_kind: string | null;
  status: 'invited' | 'active' | 'suspended';
  roles: CompanyRoleAssignmentRecord[];
};

export type CompanyUserRecord = {
  id: number;
  status: 'invited' | 'active' | 'suspended';
  invited_at?: string | null;
  activated_at?: string | null;
  suspended_at?: string | null;
  removed_at?: string | null;
  invited_by: {
    name: string;
    email: string;
  } | null;
  user: {
    id: number;
    name: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    status: string;
    email_verified_at?: string | null;
    invited_at?: string | null;
    last_login_at?: string | null;
  } | null;
  company_roles: CompanyRoleAssignmentRecord[];
  group_memberships: CompanyUserGroupMembershipRecord[];
};

export type CompanyPendingInviteRecord = {
  id: number;
  email: string;
  status: string;
  expires_at?: string | null;
  accepted_at?: string | null;
};

export type CompanyUsersResponse = {
  summary: {
    total: number;
    active: number;
    invited: number;
    suspended: number;
    pending_invitations: number;
  };
  groups: Array<{
    id: number;
    name: string;
    slug: string;
    kind: string;
    status: string;
  }>;
  data: CompanyUserRecord[];
  pending_invites: CompanyPendingInviteRecord[];
};

export type CompanyRoleCatalogEntry = {
  id: number;
  key: string;
  name: string;
  scope: string;
  description: string | null;
  assignment_count: number;
  permissions: string[];
};

export type CompanyRoleLevel = {
  id: string;
  label: string;
  description: string;
  assignable_here: boolean;
  roles: CompanyRoleCatalogEntry[];
};

export type CompanyRolesResponse = {
  summary: {
    company_roles: number;
    group_roles: number;
    platform_roles: number;
    distinct_permissions: number;
  };
  levels: CompanyRoleLevel[];
};

export type AddressFields = {
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
};

export type BillingAddressFields = AddressFields & {
  contact_name: string | null;
};

export type CompanyIndexResponse = {
  summary: {
    total: number;
    draft: number;
    active: number;
    onboarding: number;
    archived: number;
    pending_invitations: number;
    filtered_total: number;
  };
  data: {
    data: CompanyRecord[];
    current_page: number;
    last_page: number;
    total: number;
  };
};

export type CompanyMutationPayload = {
  name: string;
  slug: string;
  status?: CompanyStatus;
};

type ValidationError = {
  message?: string;
  errors?: Record<string, string[]>;
};

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const readJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new Error(`Company request failed (${response.status}).`);
  }

  return response.json() as Promise<T>;
};

export const fetchCompanies = async (): Promise<CompanyIndexResponse> => {
  const params = new URLSearchParams({
    page: '1',
    per_page: '100',
  });

  return readJson<CompanyIndexResponse>(
    await fetch(`/api/v1/secure/admin/clients?${params.toString()}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );
};

export const createCompany = async (payload: CompanyMutationPayload): Promise<CompanyRecord> => {
  const response = await fetch('/api/v1/secure/admin/clients', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(payload),
  });

  const validationError = await readValidationError(response, 'Please check the company fields and try again.');
  if (validationError) {
    throw validationError;
  }

  const responsePayload = await readJson<{ record: CompanyRecord }>(response);

  return responsePayload.record;
};

export const updateCompany = async (companyId: number, payload: CompanyMutationPayload): Promise<CompanyRecord> => {
  const response = await fetch(`/api/v1/secure/admin/clients/${companyId}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(payload),
  });

  const validationError = await readValidationError(response, 'Unable to update this client.');
  if (validationError) {
    throw validationError;
  }

  const responsePayload = await readJson<{ record: CompanyRecord }>(response);
  return responsePayload.record;
};

export const archiveCompany = async (companyId: number): Promise<CompanyRecord> => {
  const response = await fetch(`/api/v1/secure/admin/clients/${companyId}/archive`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
  });

  const responsePayload = await readJson<{ record: CompanyRecord }>(response);
  return responsePayload.record;
};

export const restoreCompany = async (companyId: number): Promise<CompanyRecord> => {
  const response = await fetch(`/api/v1/secure/admin/clients/${companyId}/restore`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
  });

  const responsePayload = await readJson<{ record: CompanyRecord }>(response);
  return responsePayload.record;
};

export const permanentlyDeleteCompany = async (
  companyId: number,
  payload: { confirmation_name: string; reason: string; confirm_permanent_delete: boolean }
): Promise<void> => {
  const response = await fetch(`/api/v1/secure/admin/clients/${companyId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorPayload = (await response.json().catch(() => ({}))) as ValidationError;
    throw new Error(errorPayload.message ?? `Company request failed (${response.status}).`);
  }
};

export const fetchCompanyProfile = async (companyId: number): Promise<CompanyProfile> => {
  const payload = await readJson<{ profile: CompanyProfile }>(
    await fetch(`/api/v1/secure/admin/clients/${companyId}/profile`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return payload.profile;
};

export const updateCompanyProfile = async (
  companyId: number,
  payload: FormData
): Promise<CompanyProfile> => {
  const response = await fetch(`/api/v1/secure/admin/clients/${companyId}/profile`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: payload,
  });

  if (response.status === 422) {
    const errorPayload = (await response.json()) as ValidationError;
    const error = new Error(errorPayload.message ?? 'Please check the company profile fields and try again.');
    (error as Error & { validation?: ValidationError }).validation = errorPayload;
    throw error;
  }

  const responsePayload = await readJson<{ profile: CompanyProfile }>(response);

  return responsePayload.profile;
};

const readValidationError = async (response: Response, fallback: string) => {
  if (response.status !== 422) {
    return null;
  }

  const errorPayload = (await response.json()) as ValidationError;
  const error = new Error(errorPayload.message ?? fallback);
  (error as Error & { validation?: ValidationError }).validation = errorPayload;

  return error;
};

export const fetchCompanyLocations = async (companyId: number): Promise<CompanyLocationRecord[]> => {
  const payload = await readJson<{ data: CompanyLocationRecord[] }>(
    await fetch(`/api/v1/secure/admin/clients/${companyId}/locations`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return payload.data;
};

export const createCompanyLocation = async (
  companyId: number,
  payload: Record<string, string | boolean>
): Promise<CompanyLocationRecord> => {
  const response = await fetch(`/api/v1/secure/admin/clients/${companyId}/locations`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(payload),
  });

  const validationError = await readValidationError(response, 'Please check the location fields and try again.');
  if (validationError) {
    throw validationError;
  }

  const responsePayload = await readJson<{ record: CompanyLocationRecord }>(response);

  return responsePayload.record;
};

export const updateCompanyLocation = async (
  companyId: number,
  locationId: number,
  payload: Record<string, string | boolean>
): Promise<CompanyLocationRecord> => {
  const response = await fetch(`/api/v1/secure/admin/clients/${companyId}/locations/${locationId}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(payload),
  });

  const validationError = await readValidationError(response, 'Please check the location fields and try again.');
  if (validationError) {
    throw validationError;
  }

  const responsePayload = await readJson<{ record: CompanyLocationRecord }>(response);

  return responsePayload.record;
};

export const deleteCompanyLocation = async (companyId: number, locationId: number): Promise<void> => {
  await readJson<{ deleted: boolean }>(
    await fetch(`/api/v1/secure/admin/clients/${companyId}/locations/${locationId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
    })
  );
};

export const fetchCompanyGroups = async (companyId: number): Promise<CompanyGroupRecord[]> => {
  const payload = await readJson<{ data: CompanyGroupRecord[] }>(
    await fetch(`/api/v1/secure/admin/clients/${companyId}/groups`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return payload.data;
};

export const createCompanyGroup = async (
  companyId: number,
  payload: Record<string, string | number | null>
): Promise<CompanyGroupRecord> => {
  const response = await fetch(`/api/v1/secure/admin/clients/${companyId}/groups`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(payload),
  });

  const validationError = await readValidationError(response, 'Please check the team or department fields and try again.');
  if (validationError) {
    throw validationError;
  }

  const responsePayload = await readJson<{ record: CompanyGroupRecord }>(response);

  return responsePayload.record;
};

export const updateCompanyGroup = async (
  companyId: number,
  groupId: number,
  payload: Record<string, string | number | null>
): Promise<CompanyGroupRecord> => {
  const response = await fetch(`/api/v1/secure/admin/clients/${companyId}/groups/${groupId}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(payload),
  });

  const validationError = await readValidationError(response, 'Please check the team or department fields and try again.');
  if (validationError) {
    throw validationError;
  }

  const responsePayload = await readJson<{ record: CompanyGroupRecord }>(response);

  return responsePayload.record;
};

export const deleteCompanyGroup = async (companyId: number, groupId: number): Promise<void> => {
  await readJson<{ deleted: boolean }>(
    await fetch(`/api/v1/secure/admin/clients/${companyId}/groups/${groupId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
    })
  );
};

export const fetchCompanyUsers = async (companyId: number): Promise<CompanyUsersResponse> =>
  readJson<CompanyUsersResponse>(
    await fetch(`/api/v1/secure/admin/clients/${companyId}/users`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

export const createCompanyUser = async (
  companyId: number,
  payload: Record<string, unknown>
): Promise<{ record: CompanyUserRecord; created_pending_user: boolean }> => {
  const response = await fetch(`/api/v1/secure/admin/clients/${companyId}/users`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(payload),
  });

  const validationError = await readValidationError(response, 'Please check the user access fields and try again.');
  if (validationError) {
    throw validationError;
  }

  return readJson<{ record: CompanyUserRecord; created_pending_user: boolean }>(response);
};

export type CompanyUserPasswordUpdateResponse = {
  email: string;
  updated: boolean;
};

export const updateCompanyUser = async (
  companyId: number,
  membershipId: number,
  payload: Record<string, unknown>
): Promise<CompanyUserRecord> => {
  const response = await fetch(`/api/v1/secure/admin/clients/${companyId}/users/${membershipId}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(payload),
  });

  const validationError = await readValidationError(response, 'Please check the user access fields and try again.');
  if (validationError) {
    throw validationError;
  }

  const responsePayload = await readJson<{ record: CompanyUserRecord }>(response);

  return responsePayload.record;
};

export const updateCompanyUserPassword = async (
  companyId: number,
  membershipId: number,
  payload: { password: string; password_confirmation: string }
): Promise<CompanyUserPasswordUpdateResponse> => {
  const response = await fetch(`/api/v1/secure/admin/clients/${companyId}/users/${membershipId}/password-reset`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(payload),
  });

  const validationError = await readValidationError(response, 'Please check the password fields and try again.');
  if (validationError) {
    throw validationError;
  }

  return readJson<CompanyUserPasswordUpdateResponse>(response);
};

export const deleteCompanyUser = async (companyId: number, membershipId: number): Promise<void> => {
  await readJson<{ deleted: boolean }>(
    await fetch(`/api/v1/secure/admin/clients/${companyId}/users/${membershipId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
    })
  );
};

export const fetchCompanyRoles = async (companyId: number): Promise<CompanyRolesResponse> =>
  readJson<CompanyRolesResponse>(
    await fetch(`/api/v1/secure/admin/clients/${companyId}/roles`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );
